/**
 * CDN Information Column Configuration
 *
 * CDN template shows rows per configoption (bandwidth types),
 * not per service like other templates.
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createTimeRangeFormatter,
} from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import { formatNumberUsage } from '@/utils/function'
import {
    ServiceStatus,
    ServiceConfigoption,
    Service,
} from '@/modules/Service/types'
import ServiceContract from '../../../shared/ServiceContract'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

// Row type for CDN table (configoption + service fields)
export type CDNRow = ServiceConfigoption &
    Partial<Service> & {
        id?: number
        entity_name?: string
    }

// ============================================================================
// Column Configuration
// ============================================================================

export const createCDNColumns = (
    t: TFunction,
    hasPrice: boolean = false
): DataTableColumn<CDNRow>[] => {
    const columns: DataTableColumn<CDNRow>[] = [
        {
            key: 'entity_name',
            title: t('entity-name'),
            width: 200,
            pinned: 'left',
            render: (value) => value || '—',
        },
        {
            key: 'name',
            title: t('bandwidth'),
            width: 310,
            pinned: 'left',
            render: (value) => t(value) || value,
        },
        {
            key: 'commitment_value',
            title: t('commitment_value'),
            width: 150,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (!value) {
                    return (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="text-muted-foreground text-xs">
                                        PAYG
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t('payg')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )
                }

                const commitmentTime = row?.commitment_time
                if (commitmentTime && commitmentTime > 1) {
                    return `${formatNumberUsage(value)}GB/ ${commitmentTime}${t('month')}`
                }
                return formatNumberUsage(value)
            },
        },
        {
            key: 'commitment_prepaid',
            title: t('commitment_prepaid.true'),
            width: 100,
            hidden: !hasPrice,
            render: (value, row) => {
                if (!row?.commitment_value || row.commitment_value <= 0)
                    return null
                return (
                    <div className="flex justify-center">
                        <Checkbox checked={!!value} disabled />
                    </div>
                )
            },
        },
        {
            key: 'commitment_price_per_unit',
            title: t('commitment_price_per_unit'),
            width: 120,
            cellClassName: 'text-right',
            render: (value, row) => {
                const price =
                    value > 0 ? value : row?.price_ranges?.[0]?.price_per_unit
                if (!price) return '-'
                return undefined // Use formatter
            },
            formatter: createCurrencyFormatter({ currency: '' }),
        },
        {
            key: 'commitment_price_total',
            title: t('commitment_price_total'),
            width: 150,
            cellClassName: 'text-right',
            hidden: !hasPrice,
            render: (value) => {
                if (!value || value <= 0) return '-'
                return undefined // Use formatter
            },
            formatter: createCurrencyFormatter({ currency: '' }),
        },
        {
            key: 'service_price_recurring',
            title: t('service_price_recurring'),
            width: 150,
            cellClassName: 'text-right',
            hidden: !hasPrice,
            render: (value) => {
                if (!value || value <= 0) return '-'
                return undefined // Use formatter
            },
            formatter: createCurrencyFormatter({ currency: '' }),
        },
        {
            key: 'employee_names',
            title: t('employee_sale_tracking_ids'),
            width: 200,
            render: (_, row) => row?.employee_names?.join(', ') || '-',
        },
        {
            key: 'commitment_time',
            title: t('commitment_time'),
            width: 100,
            hidden: !hasPrice,
            cellClassName: 'text-right',
            render: (value) => value || '-',
        },
        {
            key: 'service_start_date',
            title: t('service_start_date'),
            width: 110,
            cellClassName: 'text-right',
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'service_active_cycling',
            title: t('service_active_cycling'),
            width: 180,
            cellClassName: 'text-right',
            formatter: createTimeRangeFormatter({
                startKey: 'current_startdate',
                endKey: 'current_enddate',
                format: 'DD/MM/YYYY',
                smartYear: true,
            }),
        },
        {
            key: 'service_renew_cycling',
            title: t('service_renew_cycling'),
            width: 180,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row?.service_status !== ServiceStatus.Active) return null
                return undefined // Use formatter
            },
            formatter: createTimeRangeFormatter({
                startKey: 'continue_startdate',
                endKey: 'continue_enddate',
                format: 'DD/MM/YYYY',
                smartYear: true,
            }),
        },
        {
            key: 'service_stopped_date',
            title: t('service_stopped_date'),
            width: 110,
            cellClassName: 'text-right',
            hidden: true, // Will be shown conditionally
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'service_contract',
            title: t('service_contract'),
            width: 300,
            render: (_, row) => {
                if (!row?.service_id) return null
                return <ServiceContract serviceID={row.service_id} />
            },
        },
    ]

    return columns
}

// ============================================================================
// Data Transformer
// ============================================================================

interface TransformCDNDataOptions {
    service: Service
    data: {
        configoptions?: ServiceConfigoption[]
        addons?: Service[]
    }
}

export const transformCDNData = ({
    service,
    data,
}: TransformCDNDataOptions): CDNRow[] => {
    if (!data) return []

    const rows: CDNRow[] = []

    // Main service configoptions
    if (data.configoptions?.length) {
        data.configoptions.forEach((configoption) => {
            rows.push({
                ...configoption,
                entity_name: service?.name,
                service_id: service?.service_id,
                service_start_date: service?.service_start_date,
                service_stopped_date: service?.service_stopped_date,
                service_status: service?.service_status,
                employee_names: service?.employee_names,
                current_startdate: service?.current_startdate,
                current_enddate: service?.current_enddate,
                continue_startdate: service?.continue_startdate,
                continue_enddate: service?.continue_enddate,
                // If only 1 configoption, show "total-bandwidth" as name
                name:
                    data.configoptions?.length === 1
                        ? 'total-bandwidth'
                        : configoption?.name,
            })
        })
    }

    // Addon services
    if (data.addons?.length) {
        data.addons.forEach((addon) => {
            rows.push({
                service_configoption_id: `addon-${addon.service_id}`,
                id: addon.service_id,
                service_id: addon.service_id,
                entity_name: addon?.name,
                name: `[ADDON] - ${addon.service_name}`,
                commitment_value:
                    addon.configoptions?.[0]?.commitment_value || 0,
                commitment_time: 1,
                commitment_price_total: addon.service_price_register,
                service_price_recurring: addon.service_price_recurring,
                service_start_date: addon.service_start_date,
                service_stopped_date: addon.service_stopped_date,
                service_status: addon.service_status,
                employee_names: service?.employee_names,
                current_startdate: addon.current_startdate,
                current_enddate: addon.current_enddate,
                continue_startdate: addon.continue_startdate,
                continue_enddate: addon.continue_enddate,
            } as CDNRow)
        })
    }

    return rows
}
